import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface PublicRestaurant {
  id: string;
  name: string;
  slug: string;
  logo_url: string | null;
  phone: string | null;
  address: string | null;
  delivery_enabled: boolean;
  pickup_enabled: boolean;
  delivery_fee: number;
  min_order_amount: number;
  delivery_time_estimate: string;
  public_menu_enabled: boolean;
  customer_ordering_enabled: boolean;
  whatsapp_number: string | null;
}

export interface PublicCategory {
  id: string;
  name: string;
  description: string | null;
  sort_order: number;
}

export interface PublicProduct {
  id: string;
  name: string;
  description: string | null;
  price: number;
  image_url: string | null;
  category_id: string | null;
}

export interface PublicTable {
  id: string;
  number: string;
  name: string | null;
  status: string;
  current_order_id: string | null;
}

export interface TableBill {
  order_id: string;
  order_number: number;
  subtotal: number;
  discount: number;
  additional_fees: number;
  total: number;
  status: string;
  created_at: string;
}

export interface TableOrderItem {
  id: string;
  product_name: string;
  quantity: number;
  unit_price: number;
  total_price: number;
  notes: string | null;
}

export function usePublicMenu(slug: string, channel: "delivery" | "table" | "tablet" | "pdv" = "delivery") {
  const [restaurant, setRestaurant] = useState<PublicRestaurant | null>(null);
  const [categories, setCategories] = useState<PublicCategory[]>([]);
  const [products, setProducts] = useState<PublicProduct[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchMenu() {
      if (!slug) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        // Buscar restaurante pelo slug
        const { data: restaurantData, error: restaurantError } = await supabase
          .rpc("get_public_restaurant", { p_slug: slug });

        if (restaurantError) throw restaurantError;

        if (!restaurantData || restaurantData.length === 0) {
          setError("Restaurante não encontrado ou cardápio não disponível");
          setIsLoading(false);
          return;
        }

        const rest = restaurantData[0] as PublicRestaurant;
        setRestaurant(rest);

        // Buscar categorias
        const { data: categoriesData, error: categoriesError } = await supabase
          .rpc("get_public_categories", { p_restaurant_id: rest.id });

        if (categoriesError) throw categoriesError;
        setCategories((categoriesData as PublicCategory[]) || []);

        // Buscar produtos filtrados pelo canal
        const { data: productsData, error: productsError } = await supabase
          .rpc("get_public_products", { p_restaurant_id: rest.id, p_channel: channel });

        if (productsError) throw productsError;
        setProducts((productsData as PublicProduct[]) || []);
      } catch (err) {
        console.error("Erro ao buscar cardápio:", err);
        setError("Erro ao carregar cardápio");
      } finally {
        setIsLoading(false);
      }
    }

    fetchMenu();
  }, [slug, channel]);

  return {
    restaurant,
    categories,
    products,
    isLoading,
    error,
  };
}

export function useTableMenu(slug: string, tableNumber: string) {
  const { restaurant, categories, products, isLoading: menuLoading, error: menuError } = usePublicMenu(slug, "table");
  const [table, setTable] = useState<PublicTable | null>(null);
  const [bill, setBill] = useState<TableBill | null>(null);
  const [orderItems, setOrderItems] = useState<TableOrderItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function fetchTableData() {
      if (!restaurant || !tableNumber) {
        setIsLoading(false);
        return;
      }

      try {
        // Buscar mesa
        const { data: tableData, error: tableError } = await supabase
          .rpc("get_public_table", { 
            p_restaurant_id: restaurant.id, 
            p_table_number: tableNumber 
          });

        if (tableError) throw tableError;

        if (!tableData || tableData.length === 0) {
          setIsLoading(false);
          return;
        }

        const tableInfo = tableData[0] as PublicTable;
        setTable(tableInfo);

        // Se houver pedido aberto, buscar detalhes
        if (tableInfo.current_order_id) {
          const { data: billData, error: billError } = await supabase
            .rpc("get_table_bill", { p_order_id: tableInfo.current_order_id });

          if (billError) throw billError;
          if (billData && billData.length > 0) {
            setBill(billData[0] as TableBill);
          }

          const { data: itemsData, error: itemsError } = await supabase
            .rpc("get_table_order_items", { p_order_id: tableInfo.current_order_id });

          if (itemsError) throw itemsError;
          setOrderItems((itemsData as TableOrderItem[]) || []);
        }
      } catch (err) {
        console.error("Erro ao buscar dados da mesa:", err);
      } finally {
        setIsLoading(false);
      }
    }

    if (!menuLoading && restaurant) {
      fetchTableData();
    }
  }, [restaurant, tableNumber, menuLoading]);

  const refetchBill = async () => {
    if (!table?.current_order_id && !restaurant) return;

    try {
      // Buscar mesa atualizada
      const { data: tableData } = await supabase
        .rpc("get_public_table", { 
          p_restaurant_id: restaurant!.id, 
          p_table_number: tableNumber 
        });

      if (tableData && tableData.length > 0) {
        const tableInfo = tableData[0] as PublicTable;
        setTable(tableInfo);

        if (tableInfo.current_order_id) {
          const { data: billData } = await supabase
            .rpc("get_table_bill", { p_order_id: tableInfo.current_order_id });

          if (billData && billData.length > 0) {
            setBill(billData[0] as TableBill);
          }

          const { data: itemsData } = await supabase
            .rpc("get_table_order_items", { p_order_id: tableInfo.current_order_id });

          setOrderItems((itemsData as TableOrderItem[]) || []);
        }
      }
    } catch (err) {
      console.error("Erro ao atualizar conta:", err);
    }
  };

  return {
    restaurant,
    categories,
    products,
    table,
    bill,
    orderItems,
    isLoading: menuLoading || isLoading,
    error: menuError,
    refetchBill,
  };
}
